/**
 * features/settings/CompanySettings.tsx
 * ------------------------------------------------------------------
 * The one real, usable screen in Phase 0. It edits the company
 * settings singleton, and it is where the VAT switch lives.
 *
 * The VAT panel demonstrates the "wired but dormant" design: today
 * the toggle is OFF, no VAT is charged, and the UI says so plainly.
 * The day Acepsle registers, flipping the switch reveals the rate and
 * VAT-number fields and — from that moment — every NEW document
 * captures a VAT-registered snapshot. Documents already issued keep
 * their original (unregistered) snapshot untouched.
 * ------------------------------------------------------------------
 */
import { useEffect, useState } from 'react';
import { Save, ShieldCheck, ShieldOff } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useSettings } from '@/contexts/SettingsContext';
import {
  Badge,
  Button,
  Card,
  Field,
  Input,
  Textarea,
} from '@/components/ui/primitives';
import {
  DEFAULT_COMPANY_SETTINGS,
  type CompanySettings as CompanySettingsType,
} from '@/types';
import { logAudit } from '@/lib/audit';

export function CompanySettings() {
  const { user } = useAuth();
  const { settings, loading, uninitialised, save } = useSettings();
  const [form, setForm] = useState<CompanySettingsType>(
    DEFAULT_COMPANY_SETTINGS,
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (settings) setForm(settings);
  }, [settings]);

  function set<K extends keyof CompanySettingsType>(
    key: K,
    value: CompanySettingsType[K],
  ) {
    setForm((f) => ({ ...f, [key]: value }));
    setSaved(false);
  }

  async function handleSave() {
    if (!user) return;
    setSaving(true);
    try {
      const before = settings ?? undefined;
      const next: CompanySettingsType = {
        ...form,
        updatedAt: new Date().toISOString(),
        updatedBy: user.email ?? user.uid,
      };
      await save(next);
      await logAudit({
        actorUid: user.uid,
        actorEmail: user.email ?? user.uid,
        action: 'settings.update',
        entityType: 'settings',
        entityId: 'company',
        before: before as unknown as Record<string, unknown> | undefined,
        after: next as unknown as Record<string, unknown>,
        meta: { vatRegistered: next.vatRegistered },
      });
      setSaved(true);
    } catch (err) {
      console.error('[settings] save failed', err);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="py-20 text-center text-gray-400">Loading settings…</div>;
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {uninitialised && (
        <div className="rounded-xl bg-brandSoft px-4 py-3 text-sm text-brand">
          No settings saved yet. Review the details below and press
          <span className="font-semibold"> Save settings </span>
          to initialise the company profile.
        </div>
      )}

      {/* Company identity */}
      <Card>
        <h3 className="mb-4 text-base font-semibold text-primary dark:text-gray-100">
          Company details
        </h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Legal name">
            <Input
              value={form.legalName}
              onChange={(e) => set('legalName', e.target.value)}
            />
          </Field>
          <Field label="Trading name (optional)">
            <Input
              value={form.tradingName ?? ''}
              onChange={(e) => set('tradingName', e.target.value)}
            />
          </Field>
          <Field label="Registration number">
            <Input
              value={form.registrationNumber}
              onChange={(e) => set('registrationNumber', e.target.value)}
            />
          </Field>
          <Field label="BURS TIN (optional)" hint="Seller TIN shown on invoices.">
            <Input
              value={form.tin ?? ''}
              onChange={(e) => set('tin', e.target.value)}
            />
          </Field>
          <Field label="Email">
            <Input
              type="email"
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
            />
          </Field>
          <Field label="Website (optional)">
            <Input
              value={form.website ?? ''}
              onChange={(e) => set('website', e.target.value)}
            />
          </Field>
          <Field label="Phone numbers" hint="Comma-separated.">
            <Input
              value={form.phones.join(', ')}
              onChange={(e) =>
                set(
                  'phones',
                  e.target.value
                    .split(',')
                    .map((p) => p.trim())
                    .filter(Boolean),
                )
              }
            />
          </Field>
          <Field label="Physical address">
            <Input
              value={form.physicalAddress}
              onChange={(e) => set('physicalAddress', e.target.value)}
            />
          </Field>
        </div>
      </Card>

      {/* VAT — wired but dormant */}
      <Card>
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-primary dark:text-gray-100">
                VAT status
              </h3>
              {form.vatRegistered ? (
                <Badge tone="success">Registered</Badge>
              ) : (
                <Badge tone="neutral">Not registered</Badge>
              )}
            </div>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {form.vatRegistered
                ? 'New documents will charge VAT and show your VAT number. Documents already issued keep their original VAT treatment.'
                : 'No VAT is charged. The system is fully wired for VAT — flip the switch the day you register and only new documents will carry VAT.'}
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={form.vatRegistered}
            onClick={() => set('vatRegistered', !form.vatRegistered)}
            className={`relative mt-1 inline-flex h-7 w-12 shrink-0 items-center rounded-full transition ${
              form.vatRegistered ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                form.vatRegistered ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        <div className="flex items-center gap-2 rounded-xl bg-gray-50 px-3 py-2 text-sm text-gray-600 dark:bg-white/5 dark:text-gray-300">
          {form.vatRegistered ? (
            <ShieldCheck size={16} className="text-green-600" />
          ) : (
            <ShieldOff size={16} className="text-gray-400" />
          )}
          <span>
            {form.vatRegistered
              ? 'VAT is active across invoices, quotes and credit notes.'
              : 'VAT fields are hidden on documents while unregistered.'}
          </span>
        </div>

        {form.vatRegistered && (
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label="VAT number">
              <Input
                value={form.vatNumber ?? ''}
                onChange={(e) => set('vatNumber', e.target.value)}
              />
            </Field>
            <Field label="Standard VAT rate (%)" hint="Botswana standard is 14%.">
              <Input
                type="number"
                value={form.vatRate}
                onChange={(e) => set('vatRate', Number(e.target.value))}
              />
            </Field>
            <Field label="Registration date">
              <Input
                type="date"
                value={form.vatRegistrationDate ?? ''}
                onChange={(e) => set('vatRegistrationDate', e.target.value)}
              />
            </Field>
          </div>
        )}

        {!form.vatRegistered && (
          <div className="mt-4">
            <Field
              label="Status note (optional)"
              hint="e.g. exemption-certificate reference while your application is pending."
            >
              <Input
                value={form.vatStatusNote ?? ''}
                onChange={(e) => set('vatStatusNote', e.target.value)}
              />
            </Field>
          </div>
        )}
      </Card>

      {/* Document defaults */}
      <Card>
        <h3 className="mb-4 text-base font-semibold text-primary dark:text-gray-100">
          Document defaults
        </h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Default payment terms (days)">
            <Input
              type="number"
              value={form.defaultPaymentTermDays}
              onChange={(e) =>
                set('defaultPaymentTermDays', Number(e.target.value))
              }
            />
          </Field>
          <div className="hidden sm:block" />
          <Field label="Invoice footer (optional)">
            <Textarea
              rows={2}
              value={form.invoiceFooter ?? ''}
              onChange={(e) => set('invoiceFooter', e.target.value)}
            />
          </Field>
          <Field label="Quote footer (optional)">
            <Textarea
              rows={2}
              value={form.quoteFooter ?? ''}
              onChange={(e) => set('quoteFooter', e.target.value)}
            />
          </Field>
        </div>
      </Card>

      {/* Bank details */}
      <Card>
        <h3 className="mb-4 text-base font-semibold text-primary dark:text-gray-100">
          Bank details
          <span className="ml-2 text-xs font-normal text-gray-400">
            shown on invoices as the "pay to" account
          </span>
        </h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Bank name">
            <Input
              value={form.bank?.bankName ?? ''}
              onChange={(e) =>
                set('bank', {
                  bankName: e.target.value,
                  branch: form.bank?.branch ?? '',
                  accountName: form.bank?.accountName ?? form.legalName,
                  accountNumber: form.bank?.accountNumber ?? '',
                  branchCode: form.bank?.branchCode ?? '',
                  swift: form.bank?.swift ?? '',
                })
              }
            />
          </Field>
          <Field label="Account name">
            <Input
              value={form.bank?.accountName ?? ''}
              onChange={(e) =>
                set('bank', {
                  bankName: form.bank?.bankName ?? '',
                  branch: form.bank?.branch ?? '',
                  accountName: e.target.value,
                  accountNumber: form.bank?.accountNumber ?? '',
                  branchCode: form.bank?.branchCode ?? '',
                  swift: form.bank?.swift ?? '',
                })
              }
            />
          </Field>
          <Field label="Account number">
            <Input
              value={form.bank?.accountNumber ?? ''}
              onChange={(e) =>
                set('bank', {
                  bankName: form.bank?.bankName ?? '',
                  branch: form.bank?.branch ?? '',
                  accountName: form.bank?.accountName ?? form.legalName,
                  accountNumber: e.target.value,
                  branchCode: form.bank?.branchCode ?? '',
                  swift: form.bank?.swift ?? '',
                })
              }
            />
          </Field>
          <Field label="Branch / branch code">
            <Input
              value={form.bank?.branchCode ?? ''}
              onChange={(e) =>
                set('bank', {
                  bankName: form.bank?.bankName ?? '',
                  branch: form.bank?.branch ?? '',
                  accountName: form.bank?.accountName ?? form.legalName,
                  accountNumber: form.bank?.accountNumber ?? '',
                  branchCode: e.target.value,
                  swift: form.bank?.swift ?? '',
                })
              }
            />
          </Field>
        </div>
      </Card>

      {/* Save bar */}
      <div className="surface rounded-card sticky bottom-0 flex items-center justify-end gap-3 p-4">
        {saved && (
          <span className="text-sm font-medium text-green-600">Saved ✓</span>
        )}
        <Button onClick={() => void handleSave()} disabled={saving}>
          <Save size={16} />
          {saving ? 'Saving…' : 'Save settings'}
        </Button>
      </div>
    </div>
  );
}
